import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Plus, Users, MapPin, Edit, Trash2, X, Search,
  ToggleLeft, ToggleRight, Building2, UserCog, Eye, ChevronRight, Clock, ShieldCheck, Camera, KeyRound, Download
} from 'lucide-react';
import RgpdPanel from '@/components/RgpdPanel';
import { downloadWithAuth } from '@/components/MesDonnees';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Affiche une date/heure de main courante (heure de Paris naïve, sans fuseau)
// dans un format lisible, sans dépendre du fuseau du navigateur.
const formatEventDateTime = (isoString) => {
  if (!isoString) return '';
  const [datePart, timePart] = isoString.split('T');
  const [year, month, day] = (datePart || '').split('-');
  const time = (timePart || '').slice(0, 5);
  return year ? `${day}/${month}/${year} à ${time}` : isoString;
};

// Catalogue des autorisations individuelles, doit correspondre à PERMISSIONS_CATALOG côté serveur.
// La suppression d'événements de main courante n'y figure jamais : elle reste
// interdite à tous, pour des raisons légales.
const PERMISSIONS_LIST = [
  { key: 'read_main_courante', label: 'Consulter la main courante', group: 'Main courante', defaultRoles: ['admin', 'chef_poste', 'agent', 'site', 'client'] },
  { key: 'write_main_courante', label: 'Rédiger des événements dans la main courante', group: 'Main courante', defaultRoles: ['admin', 'chef_poste', 'agent', 'site'] },
  { key: 'correct_own_events', label: 'Corriger ses propres événements (erreur de saisie)', group: 'Main courante', defaultRoles: ['admin', 'chef_poste', 'agent', 'site'] },
  { key: 'correct_others_events', label: "Corriger les événements rédigés par d'autres", group: 'Main courante', defaultRoles: ['admin', 'chef_poste'] },

  { key: 'read_consignes', label: 'Consulter les consignes', group: 'Consignes', defaultRoles: ['admin', 'chef_poste', 'agent', 'site', 'client'] },
  { key: 'write_consignes', label: 'Créer et modifier des consignes', group: 'Consignes', defaultRoles: ['admin', 'chef_poste'] },
  { key: 'sign_consignes', label: 'Signer les consignes', group: 'Consignes', defaultRoles: ['admin', 'chef_poste', 'agent'] },
  { key: 'delete_consignes', label: 'Supprimer des consignes', group: 'Consignes', defaultRoles: ['admin', 'chef_poste'] },

  { key: 'use_messagerie', label: 'Utiliser la messagerie', group: 'Messagerie', defaultRoles: ['admin', 'chef_poste', 'agent', 'client'] },
  { key: 'use_channel_messagerie', label: "Consulter les canaux d'équipe par site", group: 'Messagerie', defaultRoles: ['admin', 'chef_poste', 'agent', 'client', 'site'] },
  { key: 'write_channel_messagerie', label: "Écrire dans les canaux d'équipe par site", group: 'Messagerie', defaultRoles: ['admin', 'chef_poste', 'agent', 'client'] },
  { key: 'message_client', label: 'Contacter les clients de son site en messagerie', group: 'Messagerie', defaultRoles: ['admin', 'chef_poste'] },

  { key: 'view_control_reports', label: 'Consulter les fiches de contrôle des agents', group: 'Contrôle des agents', defaultRoles: ['admin', 'chef_poste'] },
  { key: 'create_control_reports', label: 'Créer des fiches de contrôle des agents', group: 'Contrôle des agents', defaultRoles: ['admin', 'chef_poste'] },

  { key: 'send_daily_reports', label: 'Envoyer manuellement le rapport PDF quotidien', group: 'Rapports & exports', defaultRoles: ['admin'] },
  { key: 'export_monthly_data', label: 'Exporter les données mensuelles (Excel)', group: 'Rapports & exports', defaultRoles: ['admin'] },

  { key: 'manage_sites', label: 'Créer, modifier et supprimer des sites', group: 'Administration', defaultRoles: ['admin'] },
  { key: 'manage_users', label: 'Créer, modifier et supprimer des utilisateurs', group: 'Administration', defaultRoles: ['admin'] },
  { key: 'approve_accounts', label: "Valider ou refuser les comptes en attente", group: 'Administration', defaultRoles: ['admin'] },
  { key: 'view_activity_history', label: "Consulter l'historique des activités", group: 'Administration', defaultRoles: ['admin'] },

  { key: 'view_calendar', label: 'Consulter le calendrier', group: 'Calendrier', defaultRoles: ['admin', 'chef_poste', 'agent', 'site', 'client'] },
  { key: 'manage_calendar', label: 'Ajouter et supprimer des rendez-vous/missions', group: 'Calendrier', defaultRoles: ['admin', 'chef_poste'] },

  { key: 'manage_daily_report', label: 'Remplir le reporting quotidien (sûreté, incendie, SST, nuit)', group: 'Reporting', defaultRoles: ['admin'] },
  { key: 'view_daily_report', label: 'Consulter le reporting quotidien', group: 'Reporting', defaultRoles: ['admin'] },

  { key: 'write_agent_reports', label: 'Rédiger un compte rendu', group: 'Comptes rendus', defaultRoles: ['admin', 'chef_poste', 'agent'] },
  { key: 'read_agent_reports', label: 'Consulter les comptes rendus', group: 'Comptes rendus', defaultRoles: ['admin', 'chef_poste', 'agent'] },
  { key: 'edit_agent_reports', label: "Modifier les comptes rendus rédigés par d'autres (l'auteur peut toujours modifier les siens)", group: 'Comptes rendus', defaultRoles: ['admin'] },
  { key: 'delete_agent_reports', label: 'Supprimer un compte rendu', group: 'Comptes rendus', defaultRoles: ['admin'] },

  { key: 'view_directory', label: "Consulter l'annuaire des contacts du site", group: 'Annuaire', defaultRoles: ['admin', 'chef_poste', 'agent', 'site', 'client'] },
  { key: 'manage_directory', label: "Ajouter, modifier et supprimer des contacts de l'annuaire", group: 'Annuaire', defaultRoles: ['admin', 'chef_poste'] },
];

const PERMISSION_GROUPS = ['Main courante', 'Consignes', 'Messagerie', 'Contrôle des agents', 'Rapports & exports', 'Administration', 'Calendrier', 'Reporting', 'Comptes rendus', 'Annuaire'];

const ROLE_MAP = {
  admin: { label: 'Administrateur', color: 'bg-rose-500/90 text-white', dot: 'bg-rose-500' },
  chef_poste: { label: 'Chef de Poste', color: 'bg-sky-500/90 text-white', dot: 'bg-sky-500' },
  agent: { label: 'Agent', color: 'bg-emerald-500/90 text-white', dot: 'bg-emerald-500' },
  client: { label: 'Client', color: 'bg-violet-500/90 text-white', dot: 'bg-violet-500' },
  site: { label: 'Site', color: 'bg-amber-500/90 text-white', dot: 'bg-amber-500' }
};

// Ordre d'affichage des rôles dans les listes d'utilisateurs
const ROLE_ORDER = ['admin', 'chef_poste', 'agent', 'site', 'client'];
const sortByRole = (list) =>
  [...list].sort((a, b) => {
    const roleDiff = ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
    if (roleDiff !== 0) return roleDiff;
    return (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' });
  });

// Affiche la vraie photo de profil d'un utilisateur dans la liste, avec
// repli sur l'initiale de son nom si aucune photo n'est enregistrée.
const UserAvatar = ({ u, size = 'w-11 h-11', inactive = false }) => {
  const [photoUrl, setPhotoUrl] = useState(null);

  useEffect(() => {
    let objectUrl = null;
    const load = async () => {
      const token = localStorage.getItem('token');
      try {
        const profRes = await fetch(`${BACKEND_URL}/api/profile/${u.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (profRes.ok) {
          const prof = await profRes.json();
          if (prof.photo_file_id) {
            const fileRes = await fetch(`${BACKEND_URL}/api/files/${prof.photo_file_id}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (fileRes.ok) {
              const blob = await fileRes.blob();
              objectUrl = URL.createObjectURL(blob);
              setPhotoUrl(objectUrl);
            }
          }
        }
      } catch { /* silencieux */ }
    };
    load();
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [u.id]);

  if (photoUrl) {
    return <img src={photoUrl} alt={u.name} className={`${size} rounded-full object-cover shrink-0 shadow`} />;
  }

  return (
    <div className={`${size} rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${inactive ? 'bg-slate-300' : 'bg-gradient-to-br from-blue-500 to-indigo-600'}`}>
      {u.name?.charAt(0)?.toUpperCase()}
    </div>
  );
};

const AdminPanel = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [missedEos, setMissedEos] = useState([]);
  const [showAgentHistory, setShowAgentHistory] = useState(false);
  const [selectedAgentForHistory, setSelectedAgentForHistory] = useState('');
  const [agentHistorySearch, setAgentHistorySearch] = useState('');
  const [loading, setLoading] = useState(true);

  // User form
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '', email: '', phone: '', password: '', role: 'agent', assigned_sites: [], site_roles: {}, can_message_client: null, job_title: '', trombinoscope_category: null, hierarchy_rank: null
  });

  // Fenêtre pop-up de gestion des permissions individuelles
  const [permissionsUser, setPermissionsUser] = useState(null);
  const [permissionsDraft, setPermissionsDraft] = useState({});
  // Portée en cours d'édition : 'global' (tous les sites) ou un site_id précis
  const [permissionsScope, setPermissionsScope] = useState('global');
  const [savingPermissions, setSavingPermissions] = useState(false);

  // Fenêtre pop-up de gestion de la photo de profil (accessible par l'admin
  // pour n'importe quel compte)
  const [photoUser, setPhotoUser] = useState(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Site form
  const [showSiteForm, setShowSiteForm] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [siteForm, setSiteForm] = useState({
    name: '', address: '', auto_pdf_enabled: false, auto_pdf_emails: [], auto_pdf_send_time: '08:00', auto_pdf_days: null, boutiques: [],
    eos_day_deadline: '21:00', eos_night_deadline: '08:30',
    eos_night_shift_start: '20:00', eos_night_shift_end: '05:00'
  });
  const [emailInput, setEmailInput] = useState('');

  // Site users modal
  const [viewingSiteUsers, setViewingSiteUsers] = useState(null);
  const [siteUsers, setSiteUsers] = useState([]);

  // Filters
  const [userSearch, setUserSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [siteFilter, setSiteFilter] = useState('all');
  const [siteSearch, setSiteSearch] = useState('');

  const token = localStorage.getItem('token');
  const authHeaders = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...authHeaders, 'Content-Type': 'application/json' };

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [usersRes, sitesRes, missedEosRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/users`, { headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/sites`, { headers: authHeaders }),
        fetch(`${BACKEND_URL}/api/admin/missed-end-of-service`, { headers: authHeaders })
      ]);
      if (usersRes.ok) setUsers(await usersRes.json());
      if (sitesRes.ok) setSites(await sitesRes.json());
      if (missedEosRes.ok) setMissedEos(await missedEosRes.json());
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const unreviewedEosCount = missedEos.filter(e => !e.reviewed).length;

  const acknowledgeEos = async (eventId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/missed-end-of-service/${eventId}/acknowledge`, {
        method: 'PATCH',
        headers: authHeaders
      });
      if (res.ok) {
        setMissedEos(prev => prev.map(e => e.id === eventId ? { ...e, reviewed: true } : e));
      } else {
        toast.error('Erreur lors de la validation');
      }
    } catch { toast.error('Erreur de connexion'); }
  };

  const acknowledgeAllEos = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/missed-end-of-service/acknowledge-all`, {
        method: 'POST',
        headers: authHeaders
      });
      if (res.ok) {
        setMissedEos(prev => prev.map(e => ({ ...e, reviewed: true })));
        toast.success('Tout est validé');
      } else {
        toast.error('Erreur lors de la validation');
      }
    } catch { toast.error('Erreur de connexion'); }
  };

  // Filtered users
  const filteredUsers = useMemo(() => {
    const filtered = users.filter(u => {
      const matchSearch = !userSearch ||
        u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
        u.email?.toLowerCase().includes(userSearch.toLowerCase());
      const matchRole = roleFilter === 'all' || u.role === roleFilter;
      const matchSite = siteFilter === 'all' || (u.assigned_sites || []).includes(siteFilter);
      return matchSearch && matchRole && matchSite;
    });
    return sortByRole(filtered);
  }, [users, userSearch, roleFilter, siteFilter]);

  // Filtered sites
  const filteredSites = useMemo(() => {
    return sites.filter(s =>
      !siteSearch || s.name?.toLowerCase().includes(siteSearch.toLowerCase()) ||
      s.address?.toLowerCase().includes(siteSearch.toLowerCase())
    );
  }, [sites, siteSearch]);

  // Site name helper
  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.name || siteId;

  // ==================== USER HANDLERS ====================
  const openUserForm = (u = null) => {
    if (u) {
      setEditingUser(u);
      setUserForm({
        name: u.name, email: u.email || '', phone: u.phone || '',
        password: '', role: u.role, assigned_sites: u.assigned_sites || [], site_roles: u.site_roles || {},
        can_message_client: u.can_message_client ?? null, job_title: u.job_title || '',
        trombinoscope_category: u.trombinoscope_category || null, hierarchy_rank: u.hierarchy_rank ?? null
      });
    } else {
      setEditingUser(null);
      setUserForm({ name: '', email: '', phone: '', password: '', role: 'agent', assigned_sites: [], site_roles: {}, can_message_client: null, job_title: '', trombinoscope_category: null, hierarchy_rank: null });
    }
    setShowUserForm(true);
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    const url = editingUser ? `${BACKEND_URL}/api/users/${editingUser.id}` : `${BACKEND_URL}/api/users`;
    const method = editingUser ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(userForm) });
      if (res.ok) {
        toast.success(editingUser ? 'Utilisateur mis à jour' : 'Utilisateur créé');
        setShowUserForm(false);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch { toast.error('Erreur de connexion'); }
  };

  // ==================== PERMISSIONS POPUP ====================
  // scope = 'global' (tous les sites) ou un site_id précis : dans ce dernier
  // cas, un réglage propre à ce site (site_permission_overrides) prend le pas
  // sur le réglage global, qui lui-même prend le pas sur le rôle par défaut.
  const effectivePermission = (u, perm, scope = 'global') => {
    if (scope !== 'global') {
      const siteOverrides = (u.site_permission_overrides || {})[scope] || {};
      if (perm.key in siteOverrides) return siteOverrides[perm.key];
    }
    const overrides = u.permission_overrides || {};
    if (perm.key in overrides) return overrides[perm.key];
    if (perm.key === 'message_client' && u.can_message_client != null) return u.can_message_client;
    return perm.defaultRoles.includes(u.role);
  };

  // Petite mention sous chaque autorisation pour expliquer d'où vient la
  // valeur affichée quand elle n'a pas été explicitement réglée à cette portée.
  const permissionHint = (u, perm, scope) => {
    if (!u) return null;
    if (scope !== 'global') {
      const siteOverrides = (u.site_permission_overrides || {})[scope] || {};
      if (perm.key in siteOverrides) return null; // réglage explicite pour ce site
      const globalOverrides = u.permission_overrides || {};
      if (perm.key in globalOverrides) {
        return `Hérité du réglage global (${globalOverrides[perm.key] ? 'autorisé' : 'refusé'})`;
      }
    }
    return perm.defaultRoles.includes(u.role) ? 'Autorisé par défaut pour ce rôle' : null;
  };

  const buildDraft = (u, scope) => {
    const draft = {};
    PERMISSIONS_LIST.forEach(perm => { draft[perm.key] = effectivePermission(u, perm, scope); });
    return draft;
  };

  const openPermissions = (u) => {
    setPermissionsScope('global');
    setPermissionsDraft(buildDraft(u, 'global'));
    setPermissionsUser(u);
  };

  const changePermissionsScope = (scope) => {
    setPermissionsScope(scope);
    setPermissionsDraft(buildDraft(permissionsUser, scope));
  };

  const savePermissions = async () => {
    if (!permissionsUser) return;
    setSavingPermissions(true);
    try {
      const body = permissionsScope === 'global'
        ? { permission_overrides: permissionsDraft }
        : { site_permission_overrides: { ...(permissionsUser.site_permission_overrides || {}), [permissionsScope]: permissionsDraft } };
      const res = await fetch(`${BACKEND_URL}/api/users/${permissionsUser.id}`, {
        method: 'PUT',
        headers: jsonHeaders,
        body: JSON.stringify(body)
      });
      if (res.ok) {
        toast.success('Permissions mises à jour');
        setPermissionsUser(null);
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setSavingPermissions(false);
    }
  };

  // ==================== PHOTO DE PROFIL (par l'admin) ====================
  const openPhotoManager = async (u) => {
    setPhotoUser(u);
    setPhotoPreviewUrl(null);
    const token = localStorage.getItem('token');
    try {
      const profRes = await fetch(`${BACKEND_URL}/api/profile/${u.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (profRes.ok) {
        const prof = await profRes.json();
        if (prof.photo_file_id) {
          const fileRes = await fetch(`${BACKEND_URL}/api/files/${prof.photo_file_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (fileRes.ok) {
            const blob = await fileRes.blob();
            setPhotoPreviewUrl(URL.createObjectURL(blob));
          }
        }
      }
    } catch { /* silencieux */ }
  };

  const handleUploadUserPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !photoUser) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    setUploadingPhoto(true);
    const token = localStorage.getItem('token');
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await fetch(`${BACKEND_URL}/api/files/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (!uploadRes.ok) throw new Error();
      const uploadData = await uploadRes.json();

      const saveRes = await fetch(`${BACKEND_URL}/api/profile/${photoUser.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ photo_file_id: uploadData.id })
      });
      if (saveRes.ok) {
        setPhotoPreviewUrl(URL.createObjectURL(file));
        toast.success(`Photo de ${photoUser.name} mise à jour`);
      } else {
        toast.error('Erreur lors de l\'enregistrement');
      }
    } catch {
      toast.error('Erreur lors de l\'import');
    } finally {
      setUploadingPhoto(false);
      e.target.value = '';
    }
  };

  // Effacement RGPD : supprime le compte et ses données personnelles ; la main
  // courante et les documents à valeur probante sont conservés sans son nom.
  const handleDeleteUser = async (userId) => {
    const target = users.find(u => u.id === userId);
    const answer = window.prompt(
      `Effacement définitif de ${target?.name || 'ce compte'}.\n\n` +
      "Son compte, son profil, sa photo, ses documents et son journal de connexion seront supprimés. " +
      "Les événements de main courante, comptes rendus et autres documents à valeur probante sont conservés, " +
      "sans son nom.\n\nCette action est irréversible. Tapez SUPPRIMER pour confirmer."
    );
    if (answer !== 'SUPPRIMER') return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}?confirmation=SUPPRIMER`, { method: 'DELETE', headers: authHeaders });
      if (res.ok) { toast.success('Données personnelles effacées'); loadData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur de connexion'); }
  };

  const handleExportUser = async (u) => {
    try {
      await downloadWithAuth(`/api/users/${u.id}/donnees`, `donnees_${u.id}.json`);
      toast.success(`Données de ${u.name} téléchargées`);
    } catch { toast.error("Impossible d'exporter les données"); }
  };

  const handleToggleUser = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}/toggle-active`, { method: 'PATCH', headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadData();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur de connexion'); }
  };

  const handleApproveUser = async (userId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}/approve`, { method: 'PATCH', headers: authHeaders });
      if (res.ok) { toast.success('Compte validé avec succès'); loadData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur de connexion'); }
  };

  const handleRejectUser = async (userId) => {
    if (!window.confirm('Refuser et supprimer définitivement ce compte en attente ?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${userId}/reject`, { method: 'DELETE', headers: authHeaders });
      if (res.ok) { toast.success('Compte refusé et supprimé'); loadData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur de connexion'); }
  };

  const pendingUsers = users.filter(u => u.approved === false);

  // ==================== SITE HANDLERS ====================
  const [boutiqueInput, setBoutiqueInput] = useState('');

  const openSiteForm = (s = null) => {
    if (s) {
      setEditingSite(s);
      setSiteForm({
        name: s.name, address: s.address,
        auto_pdf_enabled: s.auto_pdf_enabled || false,
        auto_pdf_emails: s.auto_pdf_emails || [],
        auto_pdf_send_time: s.auto_pdf_send_time || '08:00',
        auto_pdf_days: s.auto_pdf_days || null,
        boutiques: s.boutiques || [],
        eos_day_deadline: s.eos_day_deadline || '21:00',
        eos_night_deadline: s.eos_night_deadline || '08:30',
        eos_night_shift_start: s.eos_night_shift_start || '20:00',
        eos_night_shift_end: s.eos_night_shift_end || '05:00'
      });
    } else {
      setEditingSite(null);
      setSiteForm({
        name: '', address: '', auto_pdf_enabled: false, auto_pdf_emails: [], auto_pdf_send_time: '08:00', auto_pdf_days: null, boutiques: [],
        eos_day_deadline: '21:00', eos_night_deadline: '08:30', eos_night_shift_start: '20:00', eos_night_shift_end: '05:00'
      });
    }
    setEmailInput('');
    setBoutiqueInput('');
    setShowSiteForm(true);
  };

  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    const url = editingSite ? `${BACKEND_URL}/api/sites/${editingSite.id}` : `${BACKEND_URL}/api/sites`;
    const method = editingSite ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, { method, headers: jsonHeaders, body: JSON.stringify(siteForm) });
      if (res.ok) {
        toast.success(editingSite ? 'Site mis à jour' : 'Site créé');
        setShowSiteForm(false);
        loadData();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur de connexion'); }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Supprimer ce site ?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/sites/${siteId}`, { method: 'DELETE', headers: authHeaders });
      if (res.ok) { toast.success('Site supprimé'); loadData(); }
      else { toast.error('Erreur'); }
    } catch { toast.error('Erreur de connexion'); }
  };

  const [resettingPinFor, setResettingPinFor] = useState(null);

  const handleResetUserPin = async (u) => {
    if (!window.confirm(`Réinitialiser le code personnel de ${u.name} ? Il devra en créer un nouveau à sa prochaine connexion.`)) return;
    setResettingPinFor(u.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/users/${u.id}/reset-pin`, { method: 'POST', headers: authHeaders });
      if (res.ok) {
        toast.success(`Code personnel de ${u.name} réinitialisé`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la réinitialisation');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setResettingPinFor(null);
    }
  };

  const handleToggleSite = async (siteId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/sites/${siteId}/toggle-active`, { method: 'PATCH', headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadData();
      } else { const err = await res.json(); toast.error(err.detail || 'Erreur'); }
    } catch { toast.error('Erreur de connexion'); }
  };

  const loadSiteUsers = async (site) => {
    setViewingSiteUsers(site);
    try {
      const res = await fetch(`${BACKEND_URL}/api/sites/${site.id}/users`, { headers: authHeaders });
      if (res.ok) setSiteUsers(sortByRole(await res.json()));
    } catch { toast.error('Erreur de chargement'); }
  };

  const addEmail = () => {
    if (emailInput && !siteForm.auto_pdf_emails.includes(emailInput)) {
      setSiteForm({ ...siteForm, auto_pdf_emails: [...siteForm.auto_pdf_emails, emailInput] });
      setEmailInput('');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 animate-fade-in" data-testid="admin-panel">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Space Grotesk' }}>
              Administration
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1">Gestion des sites et des accès utilisateurs</p>
          </div>
          <div className="flex gap-2 text-sm">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/80 dark:bg-slate-800/80 border border-slate-200">
              <Building2 className="h-4 w-4 text-slate-400 dark:text-slate-500" />
              <span className="font-semibold text-slate-700 dark:text-slate-200">{sites.length}</span>
              <span className="text-slate-400 dark:text-slate-500">sites</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/80 dark:bg-slate-800/80 border border-slate-200">
              <Users className="h-4 w-4 text-slate-400 dark:text-slate-500" />
              <span className="font-semibold text-slate-700 dark:text-slate-200">{users.length}</span>
              <span className="text-slate-400 dark:text-slate-500">utilisateurs</span>
            </div>
          </div>
        </div>

        {/* Main Tabs */}
        <Tabs defaultValue="sites" className="w-full">
          <TabsList className={`grid w-full ${user?.role === 'admin' ? 'grid-cols-4' : 'grid-cols-3'} h-auto sm:h-12 rounded-xl bg-white/70 dark:bg-slate-800/70 border border-slate-200 p-1`}>
            <TabsTrigger
              value="sites"
              className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-xs sm:text-sm font-medium px-1.5 sm:px-3 py-2"
              data-testid="admin-sites-tab"
            >
              <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
              <span className="truncate">
                <span className="sm:hidden">Sites</span>
                <span className="hidden sm:inline">Gestion des Sites</span>
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="users"
              className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-xs sm:text-sm font-medium px-1.5 sm:px-3 py-2"
              data-testid="admin-users-tab"
            >
              <UserCog className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
              <span className="truncate">
                <span className="sm:hidden">Utilisateurs</span>
                <span className="hidden sm:inline">Utilisateurs & Accès</span>
              </span>
              {pendingUsers.length > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-amber-500 text-white hover:bg-amber-500 shrink-0">{pendingUsers.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="eos"
              className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-xs sm:text-sm font-medium px-1.5 sm:px-3 py-2"
              data-testid="admin-eos-tab"
            >
              <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
              <span className="truncate">
                <span className="sm:hidden">Fins de service</span>
                <span className="hidden sm:inline">Fins de service oubliées</span>
              </span>
              {unreviewedEosCount > 0 && (
                <Badge className="ml-1 sm:ml-2 bg-red-500 text-white hover:bg-red-500 shrink-0">{unreviewedEosCount}</Badge>
              )}
            </TabsTrigger>
            {user?.role === 'admin' && (
              <TabsTrigger
                value="rgpd"
                className="rounded-lg data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-md transition-all text-xs sm:text-sm font-medium px-1.5 sm:px-3 py-2"
                data-testid="admin-rgpd-tab"
              >
                <ShieldCheck className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2 shrink-0" />
                <span className="truncate">RGPD</span>
              </TabsTrigger>
            )}
          </TabsList>

          {/* ==================== SITES TAB ==================== */}
          <TabsContent value="sites" className="mt-6 space-y-4">
            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500" />
                <Input
                  placeholder="Rechercher un site..."
                  value={siteSearch}
                  onChange={(e) => setSiteSearch(e.target.value)}
                  className="pl-9 bg-white/80 dark:bg-slate-800/80 border-slate-200"
                  data-testid="site-search"
                />
              </div>
              <Button className="btn-primary" onClick={() => openSiteForm()} data-testid="create-site-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nouveau Site
              </Button>
            </div>

            {/* Sites List */}
            <div className="space-y-3">
              {filteredSites.length === 0 ? (
                <div className="text-center py-12 text-slate-400 dark:text-slate-500">
                  <MapPin className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p>Aucun site trouvé</p>
                </div>
              ) : (
                filteredSites.map((site) => {
                  const assignedUsers = users.filter(u => (u.assigned_sites || []).includes(site.id));
                  return (
                    <div
                      key={site.id}
                      className={`group bg-white/80 dark:bg-slate-800/80 rounded-2xl border transition-all hover:shadow-lg ${
                        site.active !== false ? 'border-slate-200 hover:border-blue-300' : 'border-orange-200 bg-orange-50/30'
                      }`}
                      data-testid={`site-card-${site.id}`}
                    >
                      <div className="p-5 flex items-center gap-4">
                        {/* Icon */}
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                          site.active !== false ? 'bg-blue-50 text-blue-600' : 'bg-orange-100 text-orange-500'
                        }`}>
                          <Building2 className="h-6 w-6" />
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-slate-800 dark:text-slate-100 truncate">{site.name}</h3>
                            {site.active === false && (
                              <Badge variant="outline" className="border-orange-300 text-orange-600 text-[10px]">
                                Inactif
                              </Badge>
                            )}
                            {site.auto_pdf_enabled && (
                              <Badge variant="outline" className="border-blue-300 text-blue-600 text-[10px]">
                                PDF Auto
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{site.address}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <button
                              onClick={() => loadSiteUsers(site)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1 transition-colors"
                              data-testid={`view-site-users-${site.id}`}
                            >
                              <Users className="h-3 w-3" />
                              {assignedUsers.length} utilisateur{assignedUsers.length !== 1 ? 's' : ''}
                              <ChevronRight className="h-3 w-3" />
                            </button>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleToggleSite(site.id)}
                            title={site.active !== false ? 'Désactiver' : 'Activer'}
                            data-testid={`toggle-site-${site.id}`}
                            className="h-8 w-8 p-0"
                          >
                            {site.active !== false
                              ? <ToggleRight className="h-5 w-5 text-emerald-500" />
                              : <ToggleLeft className="h-5 w-5 text-slate-400 dark:text-slate-500" />
                            }
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => openSiteForm(site)}
                            data-testid={`edit-site-${site.id}`}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDeleteSite(site.id)}
                            data-testid={`delete-site-${site.id}`}
                            className="h-8 w-8 p-0 hover:text-rose-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* ==================== USERS TAB ==================== */}
          <TabsContent value="users" className="mt-6 space-y-4">
            {/* Comptes en attente de validation */}
            {pendingUsers.length > 0 && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="pending-users-section">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-600" />
                  <h3 className="font-semibold text-amber-900">
                    {pendingUsers.length} compte{pendingUsers.length > 1 ? 's' : ''} en attente de validation
                  </h3>
                </div>
                <div className="space-y-2">
                  {pendingUsers.map((u) => (
                    <div
                      key={u.id}
                      className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-slate-800 rounded-lg border border-amber-200 p-3"
                      data-testid={`pending-user-${u.id}`}
                    >
                      <div>
                        <p className="font-medium text-slate-800 dark:text-slate-100">{u.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{u.email || u.phone}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                          onClick={() => handleApproveUser(u.id)}
                          data-testid={`approve-user-${u.id}`}
                        >
                          Valider
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => handleRejectUser(u.id)}
                          data-testid={`reject-user-${u.id}`}
                        >
                          Refuser
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 dark:text-slate-500" />
                <Input
                  placeholder="Rechercher un utilisateur..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9 bg-white/80 dark:bg-slate-800/80 border-slate-200"
                  data-testid="user-search"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-[180px] bg-white/80 dark:bg-slate-800/80" data-testid="role-filter">
                  <SelectValue placeholder="Filtrer par rôle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les rôles</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="chef_poste">Chef de Poste</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="site">Site</SelectItem>
                </SelectContent>
              </Select>
              <Select value={siteFilter} onValueChange={setSiteFilter}>
                <SelectTrigger className="w-[200px] bg-white/80 dark:bg-slate-800/80" data-testid="site-filter">
                  <SelectValue placeholder="Filtrer par site" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les sites</SelectItem>
                  {sites.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button className="btn-primary" onClick={() => openUserForm()} data-testid="create-user-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nouvel Utilisateur
              </Button>
            </div>

            {/* Users List */}
            <div className="space-y-3">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-12 text-slate-400 dark:text-slate-500">
                  <Users className="h-12 w-12 mx-auto mb-3 opacity-40" />
                  <p>Aucun utilisateur trouvé</p>
                </div>
              ) : (
                filteredUsers.map((u) => {
                  const roleInfo = ROLE_MAP[u.role] || { label: u.role, color: 'bg-slate-500 text-white', dot: 'bg-slate-500' };
                  const isInactive = u.active === false;
                  return (
                    <div
                      key={u.id}
                      className={`group bg-white/80 dark:bg-slate-800/80 rounded-2xl border transition-all hover:shadow-lg ${
                        !isInactive ? 'border-slate-200 hover:border-blue-300' : 'border-orange-200 bg-orange-50/30'
                      }`}
                      data-testid={`user-card-${u.id}`}
                    >
                      <div className="p-5 flex items-center gap-4">
                        {/* Avatar */}
                        <UserAvatar u={u} inactive={isInactive} />

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className={`font-semibold ${isInactive ? 'text-slate-400 dark:text-slate-500 line-through' : 'text-slate-800 dark:text-slate-100'}`}>
                              {u.name}
                            </span>
                            <Badge className={`${roleInfo.color} text-[10px] px-2 py-0.5 rounded-md font-medium`}>
                              {roleInfo.label}
                            </Badge>
                            {isInactive && (
                              <Badge variant="outline" className="border-orange-300 text-orange-600 text-[10px]">
                                Désactivé
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                            {u.email && <span>{u.email}</span>}
                            {u.phone && <span>{u.phone}</span>}
                          </div>
                          {/* Assigned sites */}
                          {(u.assigned_sites || []).length > 0 && (
                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                              <MapPin className="h-3 w-3 text-slate-400 dark:text-slate-500 shrink-0" />
                              {u.assigned_sites.slice(0, 3).map(sid => {
                                const siteRole = u.site_roles?.[sid];
                                return (
                                  <span key={sid} className="text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded-md">
                                    {getSiteName(sid)}
                                    {siteRole && <span className="italic"> ({siteRole === 'chef_poste' ? 'Chef' : 'Agent'})</span>}
                                  </span>
                                );
                              })}
                              {u.assigned_sites.length > 3 && (
                                <span className="text-[10px] text-slate-400 dark:text-slate-500">+{u.assigned_sites.length - 3} autres</span>
                              )}
                            </div>
                          )}
                          {(u.assigned_sites || []).length === 0 && (
                            <p className="text-[10px] text-orange-500 mt-1.5 font-medium">Aucun site assigné</p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1.5 opacity-70 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleToggleUser(u.id)}
                            disabled={u.id === user.id}
                            title={!isInactive ? 'Désactiver' : 'Activer'}
                            data-testid={`toggle-user-${u.id}`}
                            className="h-8 w-8 p-0"
                          >
                            {!isInactive
                              ? <ToggleRight className="h-5 w-5 text-emerald-500" />
                              : <ToggleLeft className="h-5 w-5 text-slate-400 dark:text-slate-500" />
                            }
                          </Button>
                          {user.role === 'admin' && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => openPermissions(u)}
                              title="Gérer les permissions"
                              data-testid={`permissions-user-${u.id}`}
                              className="h-8 w-8 p-0"
                            >
                              <ShieldCheck className="h-4 w-4 text-indigo-500" />
                            </Button>
                          )}
                          {user.role === 'admin' && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => openPhotoManager(u)}
                              title="Modifier la photo de profil"
                              data-testid={`photo-user-${u.id}`}
                              className="h-8 w-8 p-0"
                            >
                              <Camera className="h-4 w-4 text-teal-500" />
                            </Button>
                          )}
                          {user.role === 'admin' && ['agent', 'chef_poste'].includes(u.role) && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleResetUserPin(u)}
                              disabled={resettingPinFor === u.id}
                              title="Réinitialiser le code personnel (main courante)"
                              data-testid={`reset-pin-user-${u.id}`}
                              className="h-8 w-8 p-0"
                            >
                              <KeyRound className="h-4 w-4 text-amber-500" />
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => openUserForm(u)}
                            data-testid={`edit-user-${u.id}`}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                          </Button>
                          {user.role === 'admin' && (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handleExportUser(u)}
                              title="Exporter les données de cette personne (RGPD)"
                              data-testid={`export-user-${u.id}`}
                              className="h-8 w-8 p-0"
                            >
                              <Download className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                            </Button>
                          )}
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDeleteUser(u.id)}
                            disabled={u.id === user.id || user.role !== 'admin'}
                            title={user.role === 'admin' ? 'Effacer les données personnelles (RGPD)' : "Réservé à l'administrateur"}
                            data-testid={`delete-user-${u.id}`}
                            className="h-8 w-8 p-0 hover:text-rose-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <p className="text-xs text-slate-400 dark:text-slate-500 text-right">
              {filteredUsers.length} utilisateur{filteredUsers.length !== 1 ? 's' : ''} affiché{filteredUsers.length !== 1 ? 's' : ''}
            </p>
          </TabsContent>

          {/* ==================== RGPD TAB ==================== */}
          {user?.role === 'admin' && (
            <TabsContent value="rgpd" className="mt-6 space-y-4">
              <RgpdPanel />
            </TabsContent>
          )}

          {/* ==================== FINS DE SERVICE OUBLIÉES TAB ==================== */}
          <TabsContent value="eos" className="mt-6 space-y-4">
            {missedEos.length === 0 ? (
              <Card className="glass-effect">
                <CardContent className="py-12 text-center">
                  <Clock className="h-10 w-10 text-gray-400 dark:text-slate-500 mx-auto mb-3" />
                  <p className="text-gray-600 dark:text-slate-300">Aucune fin de service oubliée pour le moment</p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Historique chronologique détaillé - limité aux 8 dernières
                    entrées, le detail complet par agent est dans la popup
                    "Historique par agent" ci-dessous */}
                <Card className="glass-effect">
                  <CardHeader className="flex flex-row items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base">Historique des clôtures automatiques</CardTitle>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setSelectedAgentForHistory(''); setAgentHistorySearch(''); setShowAgentHistory(true); }}
                        data-testid="open-agent-history-btn"
                      >
                        Historique par agent
                      </Button>
                      {unreviewedEosCount > 0 && (
                        <Button variant="outline" size="sm" onClick={acknowledgeAllEos} data-testid="acknowledge-all-eos-btn">
                          Tout valider
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {missedEos.slice(0, 8).map((e) => (
                      <div
                        key={e.id}
                        className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-2.5 rounded-lg border ${
                          e.reviewed
                            ? 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                            : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900'
                        }`}
                        data-testid={`missed-eos-${e.id}`}
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{e.user_name}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400">{getSiteName(e.site_id)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            {formatEventDateTime(e.event_datetime)}
                          </span>
                          <Badge
                            variant="outline"
                            className={`text-xs ${e.seen_by_agent ? 'text-emerald-600 border-emerald-300' : 'text-amber-600 border-amber-300'}`}
                          >
                            {e.seen_by_agent ? 'Vu par l\'agent' : 'Pas encore vu'}
                          </Badge>
                          {e.reviewed ? (
                            <Badge variant="outline" className="text-xs">Validé</Badge>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => acknowledgeEos(e.id)}
                              data-testid={`acknowledge-eos-${e.id}`}
                            >
                              Valider
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* ==================== HISTORIQUE PAR AGENT DIALOG ==================== */}
      <Dialog open={showAgentHistory} onOpenChange={setShowAgentHistory}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>Historique par agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Rechercher un agent..."
              value={agentHistorySearch}
              onChange={(e) => { setAgentHistorySearch(e.target.value); setSelectedAgentForHistory(''); }}
              data-testid="agent-history-search"
            />

            {!selectedAgentForHistory && (
              <div className="space-y-1.5 max-h-56 overflow-y-auto">
                {sortByRole(users.filter(u => (u.missed_end_of_service_count || 0) > 0))
                  .filter(u => u.name.toLowerCase().includes(agentHistorySearch.trim().toLowerCase()))
                  .sort((a, b) => (b.missed_end_of_service_count || 0) - (a.missed_end_of_service_count || 0))
                  .map(u => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => setSelectedAgentForHistory(u.id)}
                      className="w-full flex items-center justify-between gap-2 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-left"
                      data-testid={`agent-history-option-${u.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">{u.name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{ROLE_MAP[u.role]?.label || u.role}</p>
                      </div>
                      <Badge className="bg-red-500 text-white shrink-0">{u.missed_end_of_service_count}</Badge>
                    </button>
                  ))}
                {sortByRole(users.filter(u => (u.missed_end_of_service_count || 0) > 0))
                  .filter(u => u.name.toLowerCase().includes(agentHistorySearch.trim().toLowerCase())).length === 0 && (
                  <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">Aucun agent trouvé</p>
                )}
              </div>
            )}

            {selectedAgentForHistory && (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {users.find(u => u.id === selectedAgentForHistory)?.name}
                  </p>
                  <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setSelectedAgentForHistory('')}>
                    Changer d'agent
                  </Button>
                </div>
                {missedEos.filter(e => e.user_id === selectedAgentForHistory).length === 0 ? (
                  <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-4">Aucun historique pour cet agent</p>
                ) : (
                  missedEos.filter(e => e.user_id === selectedAgentForHistory).map((e) => (
                    <div
                      key={e.id}
                      className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 p-2.5 rounded-lg border ${
                        e.reviewed
                          ? 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700'
                          : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-900'
                      }`}
                      data-testid={`agent-history-${e.id}`}
                    >
                      <p className="text-sm text-slate-700 dark:text-slate-200">{getSiteName(e.site_id)}</p>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs text-slate-400 dark:text-slate-500">{formatEventDateTime(e.event_datetime)}</span>
                        <Badge
                          variant="outline"
                          className={`text-xs ${e.seen_by_agent ? 'text-emerald-600 border-emerald-300' : 'text-amber-600 border-amber-300'}`}
                        >
                          {e.seen_by_agent ? 'Vu par l\'agent' : 'Pas encore vu'}
                        </Badge>
                        {e.reviewed ? (
                          <Badge variant="outline" className="text-xs">Validé</Badge>
                        ) : (
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => acknowledgeEos(e.id)}>
                            Valider
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ==================== USER FORM DIALOG ==================== */}
      <Dialog open={showUserForm} onOpenChange={(open) => { if (!open) setShowUserForm(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>
              {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUserSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="u-name" className="text-xs font-medium text-slate-600 dark:text-slate-300">Nom complet *</Label>
              <Input id="u-name" data-testid="user-name" value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })} required />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="u-email" className="text-xs font-medium text-slate-600 dark:text-slate-300">Email</Label>
                <Input id="u-email" data-testid="user-email" type="email" value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="u-phone" className="text-xs font-medium text-slate-600 dark:text-slate-300">Téléphone</Label>
                <Input id="u-phone" data-testid="user-phone" value={userForm.phone}
                  onChange={(e) => setUserForm({ ...userForm, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="u-pw" className="text-xs font-medium text-slate-600 dark:text-slate-300">
                Mot de passe {!editingUser && '*'}
              </Label>
              <Input id="u-pw" data-testid="user-password" type="password" value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })} required={!editingUser}
                placeholder={editingUser ? 'Laisser vide pour ne pas changer' : ''} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">Rôle *</Label>
              <Select value={userForm.role} onValueChange={(v) => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrateur</SelectItem>
                  <SelectItem value="chef_poste">Chef de Poste</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="site">Site</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">
                {userForm.role === 'admin' && "Accès total à l'application"}
                {userForm.role === 'chef_poste' && "Supervision des sites assignés, création de consignes"}
                {userForm.role === 'agent' && "Rédaction main courante, consultation des consignes"}
                {userForm.role === 'client' && "Lecture seule des consignes (pas de signature), messagerie avec les responsables de ses sites"}
                {userForm.role === 'site' && "Compte partagé du site : rédaction main courante (avec agent rédacteur obligatoire), lecture seule des consignes"}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">Intitulé de poste (facultatif)</Label>
              <Input
                value={userForm.job_title || ''}
                onChange={(e) => setUserForm({ ...userForm, job_title: e.target.value })}
                placeholder="Ex: PDG, Directeur, Chef de service, Agent vidéo..."
                data-testid="job-title-input"
              />
              <p className="text-[10px] text-slate-400 dark:text-slate-500">Affiché sous le nom de la personne dans l'organigramme, si renseigné</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">Catégorie dans l'organigramme</Label>
              <Select
                value={userForm.trombinoscope_category || 'auto'}
                onValueChange={(v) => setUserForm({ ...userForm, trombinoscope_category: v === 'auto' ? null : v })}
              >
                <SelectTrigger data-testid="trombinoscope-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Automatique (selon le rôle)</SelectItem>
                  <SelectItem value="direction">Direction (visible sur tous les sites)</SelectItem>
                  <SelectItem value="chef_poste">Chef de Poste (sur ses sites assignés)</SelectItem>
                  <SelectItem value="agent">Agent (sur ses sites assignés)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-slate-400 dark:text-slate-500">
                {(userForm.trombinoscope_category || 'auto') === 'auto' && "Par défaut : un chef de poste/agent apparaît normalement, un admin ou client n'apparaît pas"}
                {userForm.trombinoscope_category === 'direction' && "Ce compte apparaîtra en \"Direction\" sur TOUS les sites, quels que soient les sites assignés — sans changer ses permissions réelles"}
                {userForm.trombinoscope_category === 'chef_poste' && "Ce compte apparaîtra comme Chef de Poste, uniquement sur les sites assignés ci-dessous — sans changer ses permissions réelles"}
                {userForm.trombinoscope_category === 'agent' && "Ce compte apparaîtra comme Agent, uniquement sur les sites assignés ci-dessous — sans changer ses permissions réelles"}
              </p>
              {userForm.trombinoscope_category === 'direction' && (
                <div className="pt-2">
                  <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">Rang au sein de la Direction</Label>
                  <Input
                    type="number"
                    min="1"
                    value={userForm.hierarchy_rank ?? ''}
                    onChange={(e) => setUserForm({ ...userForm, hierarchy_rank: e.target.value ? parseInt(e.target.value, 10) : null })}
                    placeholder="Ex: 1 pour PDG, 2 pour Directeur..."
                    data-testid="hierarchy-rank-input"
                  />
                  <p className="text-[10px] text-slate-400 dark:text-slate-500">
                    1 = le plus haut de l'organigramme. Les personnes ayant le même rang apparaissent côte à côte au même niveau (ex: plusieurs Directeurs).
                  </p>
                </div>
              )}
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">Sites assignés</Label>
              <div className="border rounded-xl p-3 max-h-40 overflow-y-auto space-y-1.5 bg-slate-50/50">
                {sites.length === 0 ? (
                  <p className="text-xs text-slate-400 dark:text-slate-500 text-center py-2">Aucun site créé</p>
                ) : (
                  sites.map((site) => (
                    <div key={site.id} className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-white dark:bg-slate-800 transition-colors">
                      <Checkbox id={`usite-${site.id}`}
                        checked={userForm.assigned_sites.includes(site.id)}
                        onCheckedChange={(checked) => {
                          const { [site.id]: _removed, ...restSiteRoles } = userForm.site_roles || {};
                          setUserForm({
                            ...userForm,
                            assigned_sites: checked
                              ? [...userForm.assigned_sites, site.id]
                              : userForm.assigned_sites.filter(id => id !== site.id),
                            site_roles: checked ? (userForm.site_roles || {}) : restSiteRoles
                          });
                        }}
                      />
                      <label htmlFor={`usite-${site.id}`} className="text-sm cursor-pointer flex-1">
                        {site.name}
                        {site.active === false && <span className="text-orange-500 text-xs ml-1">(inactif)</span>}
                      </label>
                    </div>
                  ))
                )}
              </div>
            </div>
            {['chef_poste', 'agent'].includes(userForm.role) && userForm.assigned_sites.length > 1 && (
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">Rôle par site</Label>
                <p className="text-[10px] text-slate-400 dark:text-slate-500">
                  Ce compte peut être chef de poste sur un site et seulement agent sur un autre : ce n'est pas parce qu'il est
                  chef de poste sur un site qu'il l'est partout. Laisser "Rôle par défaut" pour garder le rôle ci-dessus.
                </p>
                <div className="border rounded-xl p-3 space-y-2 bg-slate-50/50">
                  {userForm.assigned_sites.map((siteId) => (
                    <div key={siteId} className="flex items-center justify-between gap-2">
                      <span className="text-sm flex-1 truncate">{getSiteName(siteId)}</span>
                      <Select
                        value={userForm.site_roles?.[siteId] || 'default'}
                        onValueChange={(v) => {
                          const nextSiteRoles = { ...(userForm.site_roles || {}) };
                          if (v === 'default') delete nextSiteRoles[siteId];
                          else nextSiteRoles[siteId] = v;
                          setUserForm({ ...userForm, site_roles: nextSiteRoles });
                        }}
                      >
                        <SelectTrigger className="w-44" data-testid={`user-site-role-${siteId}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">Rôle par défaut ({ROLE_MAP[userForm.role]?.label})</SelectItem>
                          <SelectItem value="chef_poste">Chef de Poste</SelectItem>
                          <SelectItem value="agent">Agent</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="btn-primary flex-1" data-testid="submit-user">
                {editingUser ? 'Mettre à jour' : 'Créer'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowUserForm(false)}>Annuler</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ==================== SITE FORM DIALOG ==================== */}
      <Dialog open={showSiteForm} onOpenChange={(open) => { if (!open) setShowSiteForm(false); }}>
        <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg" style={{ fontFamily: 'Space Grotesk' }}>
              {editingSite ? 'Modifier le site' : 'Nouveau site'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSiteSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="s-name" className="text-xs font-medium text-slate-600 dark:text-slate-300">Nom du site *</Label>
              <Input id="s-name" data-testid="site-name" value={siteForm.name}
                onChange={(e) => setSiteForm({ ...siteForm, name: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="s-addr" className="text-xs font-medium text-slate-600 dark:text-slate-300">Adresse *</Label>
              <Input id="s-addr" data-testid="site-address" value={siteForm.address}
                onChange={(e) => setSiteForm({ ...siteForm, address: e.target.value })} required />
            </div>
            {/* Fin de service automatique */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium">Fin de service automatique</Label>
              <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
                Plage horaire considérée comme une prise de service de nuit, et heures au-delà
                desquelles une fin de service oubliée est clôturée automatiquement.
              </p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 dark:text-slate-300">Début de la prise de nuit</Label>
                  <Input type="time" data-testid="site-eos-night-start" value={siteForm.eos_night_shift_start}
                    onChange={(e) => setSiteForm({ ...siteForm, eos_night_shift_start: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 dark:text-slate-300">Fin de la prise de nuit</Label>
                  <Input type="time" data-testid="site-eos-night-end" value={siteForm.eos_night_shift_end}
                    onChange={(e) => setSiteForm({ ...siteForm, eos_night_shift_end: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 dark:text-slate-300">
                    Échéance prise en journée (avant {siteForm.eos_night_shift_start})
                  </Label>
                  <Input type="time" data-testid="site-eos-day" value={siteForm.eos_day_deadline}
                    onChange={(e) => setSiteForm({ ...siteForm, eos_day_deadline: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-600 dark:text-slate-300">
                    Échéance prise de nuit ({siteForm.eos_night_shift_start}-{siteForm.eos_night_shift_end})
                  </Label>
                  <Input type="time" data-testid="site-eos-night" value={siteForm.eos_night_deadline}
                    onChange={(e) => setSiteForm({ ...siteForm, eos_night_deadline: e.target.value })} />
                </div>
              </div>
            </div>
            {/* PDF Config */}
            <div className="border-t pt-4">
              <div className="flex items-center space-x-2 mb-3">
                <Checkbox id="auto-pdf" checked={siteForm.auto_pdf_enabled}
                  onCheckedChange={(checked) => setSiteForm({ ...siteForm, auto_pdf_enabled: checked })} />
                <label htmlFor="auto-pdf" className="text-sm font-medium cursor-pointer">
                  Envoi automatique PDF quotidien
                </label>
              </div>
              {siteForm.auto_pdf_enabled && (
                <div className="space-y-3 pl-6">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600 dark:text-slate-300">Heure d'envoi</Label>
                    <Input type="time" value={siteForm.auto_pdf_send_time}
                      onChange={(e) => setSiteForm({ ...siteForm, auto_pdf_send_time: e.target.value })} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600 dark:text-slate-300">Jours d'envoi</Label>
                    <div className="flex gap-1.5 flex-wrap">
                      {[['Lun', 0], ['Mar', 1], ['Mer', 2], ['Jeu', 3], ['Ven', 4], ['Sam', 5], ['Dim', 6]].map(([label, dayIdx]) => {
                        const selected = siteForm.auto_pdf_days == null || siteForm.auto_pdf_days.includes(dayIdx);
                        return (
                          <button
                            key={dayIdx}
                            type="button"
                            onClick={() => {
                              const current = siteForm.auto_pdf_days == null ? [0, 1, 2, 3, 4, 5, 6] : siteForm.auto_pdf_days;
                              const next = current.includes(dayIdx) ? current.filter((d) => d !== dayIdx) : [...current, dayIdx].sort();
                              setSiteForm({ ...siteForm, auto_pdf_days: next.length === 7 ? null : next });
                            }}
                            className={`h-8 w-10 rounded-md text-xs font-medium border transition-colors ${
                              selected
                                ? 'bg-indigo-600 border-indigo-600 text-white'
                                : 'border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                            data-testid={`auto-pdf-day-${dayIdx}`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    {siteForm.auto_pdf_days != null && siteForm.auto_pdf_days.length === 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">Aucun jour sélectionné : le rapport ne sera jamais envoyé automatiquement.</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600 dark:text-slate-300">Destinataires</Label>
                    <div className="flex gap-2">
                      <Input type="email" placeholder="email@exemple.com" value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }} />
                      <Button type="button" variant="outline" size="sm" onClick={addEmail}>Ajouter</Button>
                    </div>
                    {siteForm.auto_pdf_emails.length > 0 && (
                      <div className="space-y-1 mt-1 max-h-48 overflow-y-auto pr-1">
                        {siteForm.auto_pdf_emails.map((email, idx) => (
                          <div key={idx} className="flex items-center justify-between gap-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                            <span className="min-w-0 break-all">{email}</span>
                            <button type="button" className="shrink-0" onClick={() => {
                              setSiteForm({ ...siteForm, auto_pdf_emails: siteForm.auto_pdf_emails.filter((_, i) => i !== idx) });
                            }}>
                              <X className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500 hover:text-rose-500" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-1.5 pt-2 border-t border-slate-200 dark:border-slate-700">
                <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">Boutiques / zones suivies dans le reporting</Label>
                <p className="text-[10px] text-slate-400 dark:text-slate-500">Ex: Halle Vêtements, Chausséa, Weldom... Utilisé pour le détail des rondes par boutique.</p>
                <div className="flex gap-2">
                  <Input placeholder="Nom de la boutique" value={boutiqueInput}
                    onChange={(e) => setBoutiqueInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (boutiqueInput.trim() && !siteForm.boutiques.includes(boutiqueInput.trim())) { setSiteForm({ ...siteForm, boutiques: [...siteForm.boutiques, boutiqueInput.trim()] }); setBoutiqueInput(''); } } }} />
                  <Button type="button" variant="outline" size="sm" onClick={() => {
                    if (boutiqueInput.trim() && !siteForm.boutiques.includes(boutiqueInput.trim())) {
                      setSiteForm({ ...siteForm, boutiques: [...siteForm.boutiques, boutiqueInput.trim()] });
                      setBoutiqueInput('');
                    }
                  }}>Ajouter</Button>
                </div>
                {siteForm.boutiques.length > 0 && (
                  <div className="space-y-1 mt-1">
                    {siteForm.boutiques.map((b, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm">
                        <span>{b}</span>
                        <button type="button" onClick={() => {
                          setSiteForm({ ...siteForm, boutiques: siteForm.boutiques.filter((_, i) => i !== idx) });
                        }}>
                          <X className="h-3.5 w-3.5 text-slate-400 dark:text-slate-500 hover:text-rose-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="btn-primary flex-1" data-testid="submit-site">
                {editingSite ? 'Mettre à jour' : 'Créer'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowSiteForm(false)}>Annuler</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ==================== SITE USERS DIALOG ==================== */}
      <Dialog open={!!viewingSiteUsers} onOpenChange={(open) => { if (!open) setViewingSiteUsers(null); }}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <Building2 className="h-5 w-5 text-blue-600" />
              {viewingSiteUsers?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 dark:text-slate-400 -mt-2 mb-3">Utilisateurs affectés à ce site</p>
          {siteUsers.length === 0 ? (
            <div className="text-center py-8 text-slate-400 dark:text-slate-500">
              <Users className="h-10 w-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">Aucun utilisateur assigné</p>
            </div>
          ) : (
            <div className="space-y-2">
              {siteUsers.map(su => {
                const ri = ROLE_MAP[su.role] || { label: su.role, color: 'bg-slate-500 text-white' };
                return (
                  <div key={su.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-100">
                    <UserAvatar u={su} size="w-8 h-8" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">{su.name}</p>
                      {su.email && <p className="text-[10px] text-slate-400 dark:text-slate-500">{su.email}</p>}
                    </div>
                    <Badge className={`${ri.color} text-[10px] px-2 py-0.5 rounded-md`}>{ri.label}</Badge>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fenêtre pop-up de gestion des permissions individuelles */}
      <Dialog open={!!permissionsUser} onOpenChange={(open) => !open && setPermissionsUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-indigo-500" />
              Permissions de {permissionsUser?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Cochez ou décochez librement chaque autorisation pour ce compte précis,
            indépendamment de son rôle ({ROLE_MAP[permissionsUser?.role]?.label || permissionsUser?.role}).
          </p>
          {(permissionsUser?.assigned_sites || []).length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-600 dark:text-slate-300">Portée</Label>
              <Select value={permissionsScope} onValueChange={changePermissionsScope}>
                <SelectTrigger data-testid="permissions-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Tous les sites (par défaut)</SelectItem>
                  {permissionsUser.assigned_sites.map(sid => (
                    <SelectItem key={sid} value={sid}>{getSiteName(sid)} uniquement</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {permissionsScope !== 'global' && (
                <p className="text-[10px] text-slate-400">
                  Ces réglages ne s'appliquent que sur {getSiteName(permissionsScope)} et complètent (ou remplacent) les permissions globales ci-dessus.
                </p>
              )}
            </div>
          )}
          <div className="space-y-4 py-2 max-h-[55vh] overflow-y-auto pr-1">
            {PERMISSION_GROUPS.map(group => (
              <div key={group}>
                <h4 className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-1.5">{group}</h4>
                <div className="space-y-1">
                  {PERMISSIONS_LIST.filter(p => p.group === group).map(perm => (
                    <div key={perm.key} className="flex items-start gap-2 p-2 rounded-lg hover:bg-slate-50 dark:bg-slate-800/50">
                      <Checkbox
                        id={`perm-${perm.key}`}
                        checked={!!permissionsDraft[perm.key]}
                        onCheckedChange={(checked) => setPermissionsDraft({ ...permissionsDraft, [perm.key]: !!checked })}
                        data-testid={`permission-checkbox-${perm.key}`}
                      />
                      <Label htmlFor={`perm-${perm.key}`} className="text-sm font-normal text-slate-700 dark:text-slate-200 cursor-pointer">
                        {perm.label}
                        {permissionHint(permissionsUser, perm, permissionsScope) && (
                          <span className="text-[10px] text-slate-400 block">{permissionHint(permissionsUser, perm, permissionsScope)}</span>
                        )}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setPermissionsUser(null)}>
              Annuler
            </Button>
            <Button className="flex-1 btn-primary" onClick={savePermissions} disabled={savingPermissions} data-testid="confirm-permissions-btn">
              {savingPermissions ? 'Enregistrement...' : 'Confirmer les permissions'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fenêtre pop-up de gestion de la photo de profil */}
      <Dialog open={!!photoUser} onOpenChange={(open) => !open && setPhotoUser(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-teal-500" />
              Photo de {photoUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            {photoPreviewUrl ? (
              <img src={photoPreviewUrl} alt={photoUser?.name} className="w-32 h-32 rounded-full object-cover border-2 border-slate-200 dark:border-slate-700 shadow-md" />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gradient-to-br from-indigo-500 to-purple-700 flex items-center justify-center text-white text-3xl font-bold shadow-md">
                {photoUser?.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
            <div>
              <input
                type="file"
                id="admin-photo-upload-input"
                accept="image/*"
                onChange={handleUploadUserPhoto}
                className="hidden"
                disabled={uploadingPhoto}
                data-testid="admin-photo-upload-input"
              />
              <label htmlFor="admin-photo-upload-input">
                <Button type="button" variant="outline" disabled={uploadingPhoto} asChild>
                  <span className="cursor-pointer">
                    {uploadingPhoto ? 'Import en cours...' : (photoPreviewUrl ? 'Changer la photo' : 'Ajouter une photo')}
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminPanel;
